import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, TrialRequest } from '../lib/api';
import { labelFor, MODULE_LABELS, USE_CASE_LABELS, HOW_HEARD_LABELS } from '../lib/labels';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, Building2, Mail, Users, Calendar, Loader, Bot, Navigation, Phone, Megaphone, Smile } from 'lucide-react';

const MODULE_BADGES: Record<string, { color: string; icon: React.ReactNode }> = {
  agents: {
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: <Bot className="w-3 h-3" />,
  },
  agentic_navigation: {
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: <Navigation className="w-3 h-3" />,
  },
  phone_agents: {
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    icon: <Phone className="w-3 h-3" />,
  },
  campaigns: {
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: <Megaphone className="w-3 h-3" />,
  },
  avatar: {
    color: 'bg-rose-100 text-rose-800 border-rose-200',
    icon: <Smile className="w-3 h-3" />,
  },
};

const UNKNOWN_MODULE_BADGE: { color: string; icon: React.ReactNode } = {
  color: 'bg-gray-100 text-gray-700 border-gray-200',
  icon: null,
};

const ModuleBadge: React.FC<{ module: string }> = ({ module }) => {
  const badge = MODULE_BADGES[module] ?? UNKNOWN_MODULE_BADGE;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${badge.color}`}>
      {badge.icon} {labelFor(MODULE_LABELS, module)}
    </span>
  );
};

const fullName = (first: string | null, last: string | null) => [first, last].filter(Boolean).join(' ').trim();

/** The API's ceiling for a trial grant (api/auth/models.py ApproveTrialRequest.trial_credits le=10000). */
const TRIAL_CREDITS_MAX = 10000;

export const TrialRequests: React.FC = () => {
  const { token } = useAdminAuth();
  const [requests, setRequests] = useState<TrialRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showApproveModal, setShowApproveModal] = useState<TrialRequest | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<TrialRequest | null>(null);

  // Approval state
  const [approvalCredits, setApprovalCredits] = useState(100);
  const [approvalMaxUsers, setApprovalMaxUsers] = useState(5);
  const [approvalModules, setApprovalModules] = useState<string[]>(['agents']);

  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [rejectError, setRejectError] = useState('');

  const api = new AdminAPI(token!);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await api.getTrialRequests('pending');
      setRequests(data);
      setError('');
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      setError(`Couldn't load trial requests (${reason}). Use Refresh to try again.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const openApproveModal = (request: TrialRequest) => {
    setApprovalCredits(100);
    setApprovalMaxUsers(request.requested_max_users || 5);
    const requested = request.interested_modules || ['agents'];
    setApprovalModules(requested.length > 0 ? requested : ['agents']);
    setApproveError('');
    setShowApproveModal(request);
  };

  const toggleApprovalModule = (module: string) => {
    setApprovalModules(prev =>
      prev.includes(module) ? prev.filter(m => m !== module) : [...prev, module]
    );
  };

  const handleApprove = async () => {
    if (!showApproveModal) return;
    if (approvalModules.length === 0) {
      setApproveError('Select at least one module to enable.');
      return;
    }
    // 28 Sep: the API caps a trial grant at 10,000 (ApproveTrialRequest.trial_credits);
    // say so here rather than let the approval fail with a validation error.
    if (approvalCredits < 0 || approvalCredits > TRIAL_CREDITS_MAX) {
      setApproveError(`A free trial can have up to ${TRIAL_CREDITS_MAX.toLocaleString()} credits. Add more after approval with "Add credits".`);
      return;
    }

    try {
      setProcessing(true);
      setApproveError('');
      await api.approveTrial(
        showApproveModal.organization_id,
        approvalCredits,
        approvalMaxUsers,
        approvalModules
      );
      setShowApproveModal(null);
      loadRequests();
    } catch (err) {
      const reason = err instanceof Error && err.message ? ` (${err.message})` : '';
      setApproveError(`Couldn't approve this trial${reason}. Retry, or check the organization still exists.`);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!showRejectModal) return;
    if (!rejectionReason.trim()) {
      setRejectError('Add a reason for the rejection first.');
      return;
    }

    try {
      setProcessing(true);
      setRejectError('');
      await api.rejectTrial(showRejectModal.organization_id, rejectionReason);
      setShowRejectModal(null);
      setRejectionReason('');
      loadRequests();
    } catch (err) {
      const reason = err instanceof Error && err.message ? ` (${err.message})` : '';
      setRejectError(`Couldn't reject this trial${reason}. Retry, or check the organization still exists.`);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3 text-gray-600">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        <span>Loading trial requests…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trial requests</h1>
          <p className="text-gray-600 mt-1">Review and approve pending trial signups</p>
        </div>
        <button
          onClick={loadRequests}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        !error && (
          <div className="bg-white rounded-xl shadow border border-gray-200 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All caught up</h3>
            <p className="text-gray-600">No pending trial requests at the moment.</p>
          </div>
        )
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.organization_id}
              className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden"
            >
              {/* Main Row */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Building2 className="w-5 h-5 text-gray-400" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {request.organization_name}
                      </h3>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                        Pending
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span>{request.user_email}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{fullName(request.user_first_name, request.user_last_name) || <span className="text-gray-400 italic">No name</span>}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Building2 className="w-4 h-4" />
                        <span>{request.company_size} employees</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(request.submitted_at)}</span>
                      </div>
                    </div>

                    {/* Module Interest Badges */}
                    <div className="mt-3 flex items-center flex-wrap gap-2">
                      {(request.interested_modules || []).length > 0 ? (
                        <>
                          <span className="text-xs font-medium text-gray-500">Interested in:</span>
                          {(request.interested_modules || []).map(mod => (
                            <ModuleBadge key={mod} module={mod} />
                          ))}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No module preference specified</span>
                      )}
                      <span className="text-sm text-gray-500 ml-2">
                        • {(request.requested_max_users ?? 0).toLocaleString()} {request.requested_max_users === 1 ? 'user' : 'users'} requested
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => openApproveModal(request)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => { setRejectError(''); setShowRejectModal(request); }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Reject</span>
                    </button>
                    <button
                      onClick={() => setExpandedId(expandedId === request.organization_id ? null : request.organization_id)}
                      title={expandedId === request.organization_id ? 'Hide details' : 'Show details'}
                      aria-label={expandedId === request.organization_id ? 'Hide details' : 'Show details'}
                      aria-expanded={expandedId === request.organization_id}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {expandedId === request.organization_id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === request.organization_id && (
                <div className="border-t border-gray-200 bg-gray-50 p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Workspace URL:</span>
                      <p className="text-gray-600 mt-1">app.supragent.ai/{request.organization_slug}</p>
                    </div>
                    {request.primary_use_case && (
                      <div>
                        <span className="font-medium text-gray-700">Primary use case:</span>
                        <p className="text-gray-600 mt-1">{labelFor(USE_CASE_LABELS, request.primary_use_case)}</p>
                      </div>
                    )}
                    {request.use_case_description && (
                      <div className="col-span-2">
                        <span className="font-medium text-gray-700">Use case description:</span>
                        <p className="text-gray-600 mt-1">{request.use_case_description}</p>
                      </div>
                    )}
                    {request.current_solution && (
                      <div>
                        <span className="font-medium text-gray-700">Current solution:</span>
                        <p className="text-gray-600 mt-1">{request.current_solution}</p>
                      </div>
                    )}
                    {request.estimated_monthly_conversations && (
                      <div>
                        <span className="font-medium text-gray-700">Expected conversations:</span>
                        <p className="text-gray-600 mt-1">{request.estimated_monthly_conversations}/month</p>
                      </div>
                    )}
                    {request.estimated_monthly_visitors && (
                      <div>
                        <span className="font-medium text-gray-700">Monthly site visitors:</span>
                        <p className="text-gray-600 mt-1">{request.estimated_monthly_visitors}/month</p>
                      </div>
                    )}
                    {request.site_description && (
                      <div className="col-span-2">
                        <span className="font-medium text-gray-700">Site description:</span>
                        <p className="text-gray-600 mt-1">{request.site_description}</p>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-700">How they heard about us:</span>
                      <p className="text-gray-600 mt-1">{labelFor(HOW_HEARD_LABELS, request.how_did_you_hear)}</p>
                    </div>
                    {request.referral_source && (
                      <div>
                        <span className="font-medium text-gray-700">Referral source:</span>
                        <p className="text-gray-600 mt-1">{request.referral_source}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-1">Approve trial request</h3>
            <p className="text-gray-600 mb-1">
              Approve trial for <strong>{showApproveModal.organization_name}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-5">
              Approving creates the workspace and emails the owner their sign-in.
            </p>

            {/* Module Selection */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Modules to enable <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {Object.keys(MODULE_LABELS).map((key) => {
                  const wasRequested = (showApproveModal.interested_modules || []).includes(key);
                  const isChecked = approvalModules.includes(key);
                  return (
                    <label
                      key={key}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isChecked ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleApprovalModule(key)}
                        className="mt-0.5 h-4 w-4 text-blue-600 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <ModuleBadge module={key} />
                          {wasRequested && (
                            <span className="text-xs text-gray-400">(requested)</span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-4 mb-5">
              {/* 28 Sep (mig 0702): ONE credits field — a trial lot in the org's
                  one balance, whatever modules are on. The separate conversation
                  and navigation counters are retired. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Credits
                </label>
                <input
                  type="number"
                  min="0"
                  max={TRIAL_CREDITS_MAX}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={approvalCredits}
                  onChange={(e) => setApprovalCredits(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-500 mt-1">A free-trial grant, up to {TRIAL_CREDITS_MAX.toLocaleString()}. Everything the organisation does draws from this one balance.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Max users
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={approvalMaxUsers}
                  onChange={(e) => setApprovalMaxUsers(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>

            {approveError && (
              <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{approveError}</span>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => setShowApproveModal(null)}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={processing || approvalModules.length === 0}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {processing ? 'Approving...' : 'Approve trial'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject trial request</h3>
            <p className="text-gray-600 mb-6">
              Reject trial for <strong>{showRejectModal.organization_name}</strong>?
            </p>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Provide a clear reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>

            {rejectError && (
              <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{rejectError}</span>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason('');
                  setRejectError('');
                }}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {processing ? 'Rejecting...' : 'Reject trial'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
